<template>
  <Form method="post" class="py-14" @submit="handleSubmit">
    <template v-if="credentialToLinkOnLogin">
      <h1 class="font-bold text-center text-3xl">{{ t('auth.sign-in') }}</h1>
      <p class="text-center font-semibold">{{ t('auth.to-continue') }}</p>
    </template>
    <h1 v-else class="font-bold text-center text-3xl">
      {{ t('auth.finish-sign-in') }}
    </h1>

    <Alert
      v-if="credentialToLinkOnLogin"
      class="mt-4"
      :type="AlertType.INFO"
      icon
    >
      <p>
        {{
          t('auth.link-existing-account', { organization: organization?.name })
        }}
      </p>
      <p class="mt-2">
        {{ t('auth.link-existing-account-forgot') }}
      </p>
    </Alert>

    <div class="mt-10">
      <TextInput
        v-if="credentialToLinkOnLogin"
        v-model="email"
        :label="t('auth.email')"
        :placeholder="t('auth.email')"
        :rules="[isRequired, isEmail]"
        @update:model-value="errorMessage = ''"
      />
      <div
        v-else
        class="w-full border border-on-surface-variant rounded-full min-h-10 flex items-center justify-center gap-2 text-on-surface-variant"
      >
        <Icon :size="IconSize.LG" :name="IconName.PROFILE_CIRCLE" />
        {{ email }}
      </div>
      <PasswordInput
        v-model="password"
        :label="t('auth.enter-password')"
        :placeholder="t('auth.password')"
        :rules="[isRequired]"
        @update:model-value="errorMessage = ''"
      />
    </div>

    <div class="text-right text-primary text-sm font-bold">
      <NuxtLink
        to="/auth/reset-password"
        :target="credentialToLinkOnLogin ? '_blank' : undefined"
      >
        {{ t('auth.forgot-password') }}
      </NuxtLink>
    </div>

    <Alert v-if="errorMessage" class="mt-4" :type="AlertType.ERROR" icon>
      {{ errorMessage }}
    </Alert>

    <ExternalAuthProviders
      v-if="isLinkExpired"
      @error="handleRelinkError"
      @link-accounts="handleRelinkAttempt"
    />

    <Button
      :label="t('auth.sign-in')"
      class="w-full mt-10"
      type="submit"
      data-testid="login-submit"
    />
  </Form>
</template>

<script setup lang="ts">
import { navigateTo, ref, useRoute, onMounted } from '#imports';
import { resolveDefaultLandingUrl } from '~/composables/use-default-landing-url';
import { Form } from 'vee-validate';
import { FirebaseError } from '@firebase/app';
import {
  Button,
  Alert,
  AlertType,
  Icon,
  IconName,
  IconSize,
} from '@exfluential/ui';
import { isEmailI18n, isRequiredI18n } from '~/utils/validators';
import { useLoader } from '~/composables/use-loader';
import { PasswordInput } from '~/components/inputs/password-input';
import { useFirebase } from '~/composables/use-firebase';
import { useAuthUserStore } from '~/store/auth-user';
import {
  signInWithEmailAndPassword,
  signInWithCredential,
} from '@firebase/auth';
import {
  apiGetLoggedInUserInfo,
  apiLogin,
  apiResendInvitation,
  SelfRegisterType,
} from '~/api/auth';
import { getFirstQueryParam } from '~/utils/common';
import {
  AuthErrorCodes,
  linkWithCredential,
  type UserCredential,
} from 'firebase/auth';
import { useOrganizationStore } from '~/store/organization';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '~/store/account';
import { TextInput } from '~/components/inputs/text-input';
import { useSsoRedirect } from '~/composables/use-sso-redirect';
import { ExternalAuthProviders } from '~/pages/auth/_components/external-auth-providers';

const { t } = useI18n();

const route = useRoute();
const password = ref('');
const authUserStore = useAuthUserStore();
const accountStore = useAccountStore();
const organizationStore = useOrganizationStore();
const { organization } = storeToRefs(organizationStore);
const { credentialToLinkOnLogin } = storeToRefs(authUserStore);

const isLinkExpired = ref(false);

onMounted(() => {
  if (route.query.linkAccount === 'true') {
    const ssoRedirect = useSsoRedirect();
    const state = ssoRedirect.getState();

    if (
      state?.credentialToLink &&
      organization.value?.externalIdentityProviderData?.providerId
    ) {
      const credential = ssoRedirect.deserializeCredential(
        state.credentialToLink,
        organization.value.externalIdentityProviderData.providerId,
      );
      if (credential) {
        authUserStore.setCredentialToLinkOnLogin(credential);
      }
      ssoRedirect.clearState();
    }
  }
});

type LinkedUserCredential = UserCredential & {
  _tokenResponse?: { rawUserInfo?: string };
};

const errorMessage = ref('');
const email = defineModel<string>({ required: true });

const emit = defineEmits<{
  submit: [];
  'change-type': [value: SelfRegisterType];
}>();

const isRequired = isRequiredI18n(t);
const isEmail = isEmailI18n(t);

const getErrorMessage = (err: unknown): string => {
  if (
    err instanceof FirebaseError &&
    err.code === AuthErrorCodes.INVALID_PASSWORD
  ) {
    return t('auth.incorrect-password');
  }
  return t('auth.auth-failed');
};

const handleLinkFailure = (err: unknown) => {
  if (
    err instanceof FirebaseError &&
    err.code === AuthErrorCodes.PROVIDER_ALREADY_LINKED
  ) {
    errorMessage.value = t('auth.sso-link-failed');
    return;
  }

  isLinkExpired.value = true;
  errorMessage.value = t('auth.sso-link-expired');
};

const handleRelinkAttempt = (emailFromProvider: string) => {
  if (emailFromProvider) {
    email.value = emailFromProvider;
  }
  isLinkExpired.value = false;
  errorMessage.value = '';
};

const handleRelinkError = () => {
  errorMessage.value = t('auth.sso-error');
};

const handleSubmit = async () => {
  const firebase = useFirebase();
  let isLinking = false;

  await useLoader({
    action: async () => {
      const { user } = await signInWithEmailAndPassword(
        firebase.auth,
        email.value,
        password.value,
      );
      if (!organization.value?.slug) throw new Error('Unknown organization');
      let idToken = '';
      let rawUserInfo: Record<string, unknown> | undefined;

      if (user && credentialToLinkOnLogin.value) {
        isLinking = true;
        const linkResult: LinkedUserCredential = await linkWithCredential(
          user,
          credentialToLinkOnLogin.value,
        );
        isLinking = false;
        idToken = await linkResult.user.getIdToken();
        // eslint-disable-next-line no-underscore-dangle
        const linkedUserInfo = linkResult._tokenResponse?.rawUserInfo;

        if (linkedUserInfo) {
          rawUserInfo = JSON.parse(linkedUserInfo);
        }
      } else {
        idToken = await user.getIdToken(true);
      }

      await apiLogin(idToken, rawUserInfo);

      authUserStore.authUser = await apiGetLoggedInUserInfo();
      await accountStore.setCurrentAccount();

      // Link analytics data to user for accurate journey tracking
      const { identifyUser } = await import('~/utils/posthog');
      if (authUserStore.authUser) {
        identifyUser(authUserStore.authUser, 'password');
      }
      const redirect = getFirstQueryParam(route.query.redirect);

      emit('submit');

      if (
        organization.value.isBusinessesEnabled &&
        organization.value.organizationSettings?.businessSelectorEnabled
      ) {
        navigateTo('/business-selection');
      } else {
        navigateTo(redirect ?? resolveDefaultLandingUrl(organization.value));
      }
    },
    onError: async (err: any) => {
      if (isLinking) {
        handleLinkFailure(err);
        return;
      }
      if (err.code === AuthErrorCodes.USER_DISABLED) {
        await apiResendInvitation({ email: email.value });
        emit('change-type', SelfRegisterType.RESEND_CONFIRMATION_INVITE);
      }
      errorMessage.value = getErrorMessage(err);
    },
  });
};

const handleSsoLink = async () => {
  const firebase = useFirebase();

  await useLoader({
    action: async () => {
      if (!credentialToLinkOnLogin.value) {
        throw new Error('No credential to link');
      }
      if (!organization.value?.slug) throw new Error('Unknown organization');

      try {
        const result = await signInWithCredential(
          firebase.auth,
          credentialToLinkOnLogin.value,
        );

        const idToken = await result.user.getIdToken();
        await apiLogin(idToken);

        authUserStore.authUser = await apiGetLoggedInUserInfo();
        await accountStore.setCurrentAccount();

        const { identifyUser } = await import('~/utils/posthog');
        if (authUserStore.authUser) {
          const providerType =
            organization.value?.externalIdentityProviderData?.providerId ||
            'sso';
          identifyUser(authUserStore.authUser, `sso_${providerType}`);
        }
        const redirect = getFirstQueryParam(route.query.redirect);

        emit('submit');

        if (
          organization.value.isBusinessesEnabled &&
          organization.value.organizationSettings?.businessSelectorEnabled
        ) {
          navigateTo('/business-selection');
        } else {
          navigateTo(redirect ?? resolveDefaultLandingUrl(organization.value));
        }
      } catch (err: any) {
        // eslint-disable-next-line no-underscore-dangle
        const tokenResponse = err?._tokenResponse;
        if (tokenResponse?.idToken) {
          const { idToken, rawUserInfo: rawUserInfoString } = tokenResponse;
          const rawUserInfo = JSON.parse(rawUserInfoString || '{}');

          await apiLogin(idToken);

          authUserStore.authUser = await apiGetLoggedInUserInfo();
          await accountStore.setCurrentAccount();

          const { identifyUser } = await import('~/utils/posthog');
          if (authUserStore.authUser) {
            const providerType =
              organization.value?.externalIdentityProviderData?.providerId ||
              'sso';
            identifyUser(authUserStore.authUser, `sso_${providerType}`);
          }
          const redirect = getFirstQueryParam(route.query.redirect);

          emit('submit');

          if (
            organization.value.isBusinessesEnabled &&
            organization.value.organizationSettings?.businessSelectorEnabled
          ) {
            navigateTo('/business-selection');
          } else {
            navigateTo(
              redirect ?? resolveDefaultLandingUrl(organization.value),
            );
          }
        } else {
          throw err;
        }
      }
    },
    onError: (err: any) => {
      errorMessage.value = t('auth.sso-link-failed');
    },
  });
};
</script>
